import type { HttpsCallable } from '@angular/fire/functions';
import { reportSlowCallable } from '@core/observability/sentry';

/**
 * Wrap an already-constructed `httpsCallable` function with a timing hook
 * that reports slow invocations via `reportSlowCallable` (issue #484 — is
 * there really a cold-start problem after login, before building a warm-up
 * mechanism for one).
 *
 * Takes the resolved `HttpsCallable`, not `(functions, name)` — the raw
 * `httpsCallable(...)` factory from `@angular/fire/functions` lives in a
 * sealed ESM module that can't be spied on in tests (see
 * `FirestoreApi`'s doc comment). Keeping this function's own inputs plain —
 * a callable, a name, a getter — makes the timing/reporting logic testable
 * in isolation with a fake callable, independent of that limitation.
 *
 * `getMsSinceLogin` is a getter rather than a plain value because the wrap
 * happens once (when the callable is constructed) but each invocation needs
 * the CURRENT time-since-login, not the one at construction time.
 */
export function wrapCallableWithTiming<RequestData, ResponseData>(
  name: string,
  callable: HttpsCallable<RequestData, ResponseData>,
  getMsSinceLogin: () => number | null,
): HttpsCallable<RequestData, ResponseData> {
  const wrapped = ((data?: RequestData | null) => {
    const callStartedAt = performance.now();
    return callable(data).finally(() => {
      reportSlowCallable(name, performance.now() - callStartedAt, getMsSinceLogin());
    });
  }) as HttpsCallable<RequestData, ResponseData>;
  // `HttpsCallable` also carries `.stream(...)` (streaming callables) — no call
  // site here uses it, so it's passed through untimed rather than wrapped.
  wrapped.stream = callable.stream;
  return wrapped;
}
